import React, { useState, useEffect, useRef } from "react";
import { generateClient } from "aws-amplify/data";
import { getUrl } from "aws-amplify/storage";
import PublicLayout from "../../components/layout/PublicLayout";
import { TaggedImageGallery } from "../../components/shared";
import Modal from "react-modal";
import { fetchAuthSession } from "aws-amplify/auth";

const client = generateClient();

async function pickAuthMode() {
  try {
    const s = await fetchAuthSession();
    return s.tokens ? "userPool" : "identityPool";
  } catch {
    return "identityPool";
  }
}

const Galeria = () => {
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("Todos");
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalError, setModalError] = useState(null);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const hideButtonTimeout = useRef(null);

  // Función para manejar el movimiento del mouse dentro del modal
  const handleMouseMove = () => {
    setShowCloseButton(true);

    // Reiniciar el temporizador si ya estaba corriendo
    if (hideButtonTimeout.current) clearTimeout(hideButtonTimeout.current);

    // Configurar para ocultarlo después de 3 segundos de inactividad
    hideButtonTimeout.current = setTimeout(() => {
      setShowCloseButton(false);
    }, 3000);
  };

  // Función para ocultar el botón cuando el mouse salga del área del modal
  const handleMouseLeave = () => {
    setShowCloseButton(false);
    if (hideButtonTimeout.current) clearTimeout(hideButtonTimeout.current);
  };

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        const authMode = await pickAuthMode();
        // Obtener las imágenes desde la base de datos
        const { data: items } = await client.models.Imagen.list({ authMode });
        const fetchedImages = await Promise.all(
          items.map(async (image) => {
            const isPublica = image.isPublica;
            const url = isPublica
              ? (await getUrl({ path: image.filePath })).url.href
              : null;

            return {
              id: image.id,
              filePath: image.filePath,
              src: url,
              etiquetas: image.etiquetas.split(",").map((tag) => tag.trim()),
              isPublica,
              costo: image.costo,
            };
          }),
        );

        setImages(fetchedImages);
        setFilteredImages(fetchedImages);

        // Extraer etiquetas únicas
        const uniqueTags = [
          "Todos",
          ...new Set(fetchedImages.flatMap((image) => image.etiquetas)),
        ];
        setTags(uniqueTags);
      } catch (error) {
        console.error("Error al obtener las imágenes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  // Manejar clic en una imagen
  const handleImageClick = async (image) => {
    setModalError(null);

    if (image.isPublica) {
      setSelectedImage(image);
      return;
    }

    try {
      const hasAccess = await validateAccess(image.id);
      if (!hasAccess) {
        alert("Esta imagen requiere un donativo para verla.");
        return;
      }

      const { url } = await getUrl({ path: image.filePath });
      setSelectedImage({ ...image, src: url.href });
    } catch (error) {
      console.error("Error al validar acceso a la imagen:", error);
      setModalError("Ocurrió un error al intentar acceder a la imagen.");
    }
  };

  // Validar acceso para videos privados
  const validateAccess = async (imagenId) => {
    try {
      const user = await client.auth.getCurrentUser();
      const { data: purchase } = await client.models.Compra.get({
        userId: user.id,
        imagenId: imagenId,
      });
      return !!purchase;
    } catch (error) {
      console.error("Error al validar acceso:", error);
      return false;
    }
  };

  // Manejar el cambio de filtro
  const handleFilterChange = (tag) => {
    setSelectedTag(tag);
    if (tag === "Todos") {
      setFilteredImages(images);
    } else {
      setFilteredImages(
        images.filter((image) => image.etiquetas.includes(tag)),
      );
    }
  };

  // Cerrar el modal
  const closeModal = () => setSelectedImage(null);

  if (loading)
    return <div className="py-10 text-center">Cargando imágenes...</div>;

  return (
    <PublicLayout>
      <div className="min-h-screen bg-gray-300 p-4 pt-10 md:px-10 lg:px-20">
        <div className="mb-4 rounded-lg bg-sky-400 py-2 text-center text-xl font-semibold text-sky-900 sm:text-2xl md:text-3xl lg:text-4xl">
          Actividades sugeridas y Teoría
        </div>

        {/* Filtros */}
        <div className="mb-6 flex flex-wrap justify-center gap-4">
          {tags.map((tag) => (
            <button
              key={tag}
              onClick={() => handleFilterChange(tag)}
              className={`rounded px-4 py-2 ${
                selectedTag === tag ? "bg-blue-500 text-white" : "bg-gray-300"
              }`}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Galería de imágenes */}
        <TaggedImageGallery
          images={filteredImages}
          onImageClick={handleImageClick}
        />

        {/* Modal para visor de imágenes */}
        {selectedImage && selectedImage.src && (
          <Modal
            isOpen={!!selectedImage}
            onRequestClose={closeModal}
            contentLabel="Visor de Imagen"
            style={{
              overlay: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
              content: {
                top: "50%",
                left: "50%",
                right: "auto",
                bottom: "auto",
                transform: "translate(-50%, -50%)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "transparent",
                border: "none",
                padding: "0",
                maxWidth: "90vw",
                maxHeight: "90vh",
              },
            }}
          >
            <div
              className="relative"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              {showCloseButton && (
                <button
                  onClick={closeModal}
                  className="absolute right-2 top-2 rounded bg-red-600 px-4 py-2 text-white transition-opacity duration-300"
                >
                  Cerrar
                </button>
              )}
              <img
                src={selectedImage?.src}
                alt="Visor de Imagen"
                className="object-contain"
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  width: "auto",
                  height: "auto",
                }}
              />
            </div>
          </Modal>
        )}

        {/* Mensaje de error en el modal */}
        {modalError && (
          <div className="mt-4 text-center text-red-500">{modalError}</div>
        )}
      </div>
    </PublicLayout>
  );
};

export default Galeria;
